import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight, ShieldCheck, Scale, Clock } from 'lucide-react';
import { submitLeadWithGeoLocation, getCountryInfo } from '../services/centralLeadApi';
import { toast } from 'sonner';
import countryCodes from '../data/countryCodes';
import CountryCodeSelect from './CountryCodeSelect';

const HeroFormSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', description: '' });
  const [countryCode, setCountryCode] = useState('+1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({ email: '', phone: '' });

  useEffect(() => {
    const fetchGeo = async () => {
      try {
        const info = await getCountryInfo();
        if (info) { const match = countryCodes.find(c => c.country === info.countryCode); if (match) setCountryCode(match.code); }
      } catch (e) { console.error('Geo fetch error:', e); }
    };
    fetchGeo();
  }, []);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => phone.replace(/[^\d]/g, '').length === 10;
  const handlePhoneChange = (e) => { const v = e.target.value.replace(/[^0-9\s\-]/g, ''); const d = v.replace(/[^\d]/g, ''); if (d.length > 10) return; setFormData({ ...formData, phone: v }); if (errors.phone && d.length === 10) setErrors(prev => ({ ...prev, phone: '' })); };
  const handleEmailChange = (e) => { setFormData({ ...formData, email: e.target.value }); if (errors.email && validateEmail(e.target.value)) setErrors(prev => ({ ...prev, email: '' })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ne = { email: '', phone: '' }; let h = false;
    if (!validateEmail(formData.email)) { ne.email = 'Please provide a valid email'; h = true; }
    if (!validatePhone(formData.phone)) { ne.phone = 'Enter a 10-digit number'; h = true; }
    if (h) { setErrors(ne); toast.error('Please correct the highlighted fields'); return; }
    setIsSubmitting(true);
    try {
      const ld = { name: formData.name, email: formData.email, phone: formData.phone, countryCode, country: countryCodes.find(c => c.code === countryCode)?.country || 'US', description: formData.description || 'Hero Form' };
      const r = await submitLeadWithGeoLocation(ld, null, 'Home Form Section');
      if (r.success) { toast.success(r.message || 'Submitted. We will contact you shortly.'); setFormData({ name: '', email: '', phone: '', description: '' }); }
      else { toast.error(r.message || 'Failed.'); }
    } catch (error) { toast.error('Failed. Please try again.'); }
    setIsSubmitting(false);
  };

  return (
    <section className="relative" data-testid="home-form-section">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(https://images.unsplash.com/photo-1708778002586-37c4f23b7f3f?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85)` }}
      />
      <div className="absolute inset-0 bg-[#1A3628]/90" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-28">
        <div className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-16 items-center">

          {/* Left content */}
          <div className="text-left">
            <p className="text-xs font-body font-medium uppercase tracking-[0.2em] text-[#C85A40] mb-6">Intellectual Property Attorneys</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-8 tracking-tighter leading-tight">
              Trademark Registration <span className="text-[#C85A40]">from $45</span>
            </h2>
            <p className="font-body text-base text-white/60 mb-10 leading-relaxed max-w-lg">
              Protect your brand, logo, and slogan with comprehensive USPTO registration. Experienced IP attorneys and highly professional services which brings in trust from enterprises.
            </p>

            {/* 3 Key Points */}
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ShieldCheck className="h-5 w-5 text-[#C85A40]" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="font-display text-sm font-semibold text-white mb-1">IP Protection Nationwide</h4>
                  <p className="font-body text-sm text-white/50 leading-relaxed">Exclusive rights to your business name, logo, and slogan across all 50 states.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Scale className="h-5 w-5 text-[#C85A40]" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="font-display text-sm font-semibold text-white mb-1">Experienced IP Attorneys</h4>
                  <p className="font-body text-sm text-white/50 leading-relaxed">Every application reviewed by experienced IP attorneys having 15+ years combined experience.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock className="h-5 w-5 text-[#C85A40]" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="font-display text-sm font-semibold text-white mb-1">Filed Within 24-48 Hours</h4>
                  <p className="font-body text-sm text-white/50 leading-relaxed">Fast-track filing with real-time status updates and dedicated case manager.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="w-full">
            <div className="bg-white rounded-3xl border border-[#D5D1C8] p-8" data-testid="home-form-card">
              <p className="eyebrow mb-2">Schedule a Consultation</p>
              <h3 className="font-display text-2xl font-medium text-[#111C16] mb-1">Get Started Today</h3>
              <p className="font-body text-sm text-[#4A5D53] mb-6">Our attorneys will review within 24 hours.</p>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="home-hero-form">
                <input type="text" placeholder="Full Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="bg-transparent border-b-2 border-[#D5D1C8] focus:border-[#1A3628] outline-none py-3 w-full font-body text-sm text-[#111C16] placeholder:text-[#4A5D53]/60 transition-colors" data-testid="home-form-name" />
                <div><input type="email" placeholder="Email Address" value={formData.email} onChange={handleEmailChange} onBlur={() => { if (formData.email && !validateEmail(formData.email)) setErrors(p => ({...p, email: 'Please provide a valid email'})); else setErrors(p => ({...p, email: ''})); }} required className={`bg-transparent border-b-2 ${errors.email ? 'border-red-500' : 'border-[#D5D1C8]'} focus:border-[#1A3628] outline-none py-3 w-full font-body text-sm text-[#111C16] placeholder:text-[#4A5D53]/60 transition-colors`} data-testid="home-form-email" />{errors.email && <p className="text-red-500 text-xs mt-1 font-body">{errors.email}</p>}</div>
                <div><div className="flex gap-3 items-end"><CountryCodeSelect value={countryCode} onChange={setCountryCode} countryCodes={countryCodes} className="w-24" testId="home-form-country" /><input type="tel" placeholder="Phone Number" value={formData.phone} onChange={handlePhoneChange} onBlur={() => { const d = formData.phone.replace(/[^\d]/g, ''); if (formData.phone && d.length !== 10) setErrors(p => ({...p, phone: '10-digit number required'})); else setErrors(p => ({...p, phone: ''})); }} required className={`flex-1 bg-transparent border-b-2 ${errors.phone ? 'border-red-500' : 'border-[#D5D1C8]'} focus:border-[#1A3628] outline-none py-3 font-body text-sm text-[#111C16] placeholder:text-[#4A5D53]/60 transition-colors`} data-testid="home-form-phone" /></div>{errors.phone && <p className="text-red-500 text-xs mt-1 font-body">{errors.phone}</p>}</div>
                <textarea placeholder="What would you like to protect?" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required rows={2} className="bg-transparent border-b-2 border-[#D5D1C8] focus:border-[#1A3628] outline-none py-3 w-full font-body text-sm text-[#111C16] placeholder:text-[#4A5D53]/60 resize-none transition-colors" data-testid="home-form-desc" />
                <Button type="submit" disabled={isSubmitting} className="w-full bg-[#C85A40] hover:bg-[#A64730] text-white btn-hover" data-testid="home-form-submit">
                  {isSubmitting ? 'Processing...' : 'Register Your Trademark '}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroFormSection;
